/* ==========================================================================
   Product Collection Motion Pro
   File: assets/product-collection-motion-pro-slider.js
   ========================================================================== */

(() => {
  'use strict';

  /* ==========================================================================
     01. GLOBAL CONSTANTS / HELPERS
     ========================================================================== */

  const DESKTOP_MEDIA_QUERY = '(min-width: 990px)';
  const AUTOPLAY_RESUME_DELAY = 2600;
  const TRANSITION_FALLBACK_PADDING = 80;
  const DRAG_INTENT_THRESHOLD = 6;
  const DRAG_CANCEL_Y_EXTRA = 4;

  const safeCall = (fn, ...args) => {
    try {
      if (typeof fn === 'function') return fn(...args);
    } catch (_) {}
    return undefined;
  };

  const matchesDesktop = () => {
    return !!(window.matchMedia && window.matchMedia(DESKTOP_MEDIA_QUERY).matches);
  };

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const parseGap = (element) => {
    if (!element) return 0;
    const styles = window.getComputedStyle(element);
    const gap = parseFloat(styles.columnGap || styles.gap || '0');
    return Number.isFinite(gap) ? gap : 0;
  };

  const parseTimeTokenToMs = (token) => {
    if (!token || typeof token !== 'string') return 0;

    const value = token.trim();
    if (!value) return 0;

    if (value.endsWith('ms')) {
      const ms = parseFloat(value);
      return Number.isFinite(ms) ? ms : 0;
    }

    if (value.endsWith('s')) {
      const seconds = parseFloat(value);
      return Number.isFinite(seconds) ? seconds * 1000 : 0;
    }

    const raw = parseFloat(value);
    return Number.isFinite(raw) ? raw : 0;
  };

  const getFirstTransitionTimeMs = (element, propertyName = 'transform') => {
    if (!element) return 0;

    const styles = window.getComputedStyle(element);
    const properties = (styles.transitionProperty || '')
      .split(',')
      .map((item) => item.trim());

    const durations = (styles.transitionDuration || '')
      .split(',')
      .map((item) => parseTimeTokenToMs(item));

    const delays = (styles.transitionDelay || '')
      .split(',')
      .map((item) => parseTimeTokenToMs(item));

    if (!properties.length) return 0;

    let propertyIndex = properties.findIndex((item) => {
      return item === propertyName || item === 'all';
    });

    if (propertyIndex < 0) propertyIndex = 0;

    const duration = durations[propertyIndex] ?? durations[0] ?? 0;
    const delay = delays[propertyIndex] ?? delays[0] ?? 0;

    return Math.max(0, duration + delay);
  };

  /* ==========================================================================
     02. PRODUCT SLIDER CLASS
     ========================================================================== */

  class PCMPProductSlider {
    constructor(context) {
      this.context = context;
      this.root = context?.root || null;
      this.section = context?.elements?.slider || null;
      this.viewport = context?.elements?.viewport || null;
      this.track = context?.elements?.track || null;
      this.prevButton = context?.elements?.prevButton || null;
      this.nextButton = context?.elements?.nextButton || null;
      this.settings = context?.settings || {};

      this.originalSlides = [];
      this.renderedSlides = [];
      this.currentIndex = 0;
      this.stepPx = 0;
      this.visibleColumns = 1;
      this.autoplayDirection = 1; /* 1 = next, -1 = prev */

      this.isMounted = false;
      this.isDestroyed = false;
      this.isDragging = false;
      this.isAnimating = false;
      this.hoverPaused = false;
      this.editorSelected = false;
      this.sectionVisible = true;
      this.documentVisible = document.visibilityState !== 'hidden';
      this.prefersReducedMotion = !!context?.state?.prefersReducedMotion;

      this.dragState = {
        pointerId: null,
        startX: 0,
        startY: 0,
        currentX: 0,
        deltaX: 0,
        deltaY: 0,
        startTime: 0,
        baseTranslateX: 0,
        intent: null,
        moved: false,
      };

      this.autoplayTimer = null;
      this.autoplayResumeTimer = null;
      this.transitionFallbackTimer = null;
      this.layoutRaf = null;
      this.resizeObserver = null;
      this.clickGuardUntil = 0;

      this.onPrevClick = this.onPrevClick.bind(this);
      this.onNextClick = this.onNextClick.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onPointerCancel = this.onPointerCancel.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onMouseEnter = this.onMouseEnter.bind(this);
      this.onMouseLeave = this.onMouseLeave.bind(this);
      this.onTrackTransitionEnd = this.onTrackTransitionEnd.bind(this);
      this.onTrackTransitionCancel = this.onTrackTransitionCancel.bind(this);
      this.onClickCapture = this.onClickCapture.bind(this);
      this.onWindowResize = this.onWindowResize.bind(this);
      this.onDocumentVisibilityChange = this.onDocumentVisibilityChange.bind(this);
      this.onResizeObserved = this.onResizeObserved.bind(this);
    }

    /* ==========================================================================
       03. PUBLIC LIFECYCLE
       ========================================================================== */

    mount() {
      if (this.isMounted || this.isDestroyed) return;
      if (!this.section || !this.viewport || !this.track) return;

      this.refreshElements();
      this.bind();
      this.setupObservers();
      this.rebuild({
        preserveIndex: false,
        index: 0,
      });

      this.isMounted = true;
    }

    refresh(context) {
      if (this.isDestroyed) return;

      const previousIndex = this.currentIndex;

      if (context) {
        this.context = context;
        this.root = context?.root || this.root;
        this.section = context?.elements?.slider || this.section;
        this.viewport = context?.elements?.viewport || this.viewport;
        this.track = context?.elements?.track || this.track;
        this.prevButton = context?.elements?.prevButton || this.prevButton;
        this.nextButton = context?.elements?.nextButton || this.nextButton;
        this.settings = context?.settings || this.settings;
        this.prefersReducedMotion = !!context?.state?.prefersReducedMotion;
      }

      this.refreshElements();
      this.teardownObservers();
      this.setupObservers();

      this.rebuild({
        preserveIndex: true,
        index: previousIndex,
      });
    }

    destroy() {
      if (this.isDestroyed) return;

      this.isDestroyed = true;
      this.isMounted = false;

      this.stopAutoplay();
      this.clearAutoplayResume();
      this.clearTransitionFallback();
      this.clearScheduledLayout();

      this.unbind();
      this.teardownObservers();
      this.setDraggingState(false);

      if (this.track) {
        this.track.style.transform = 'translate3d(0, 0, 0)';
        this.track.style.transitionDuration = '';
      }

      this.originalSlides = [];
      this.renderedSlides = [];
    }

    setSectionVisible(isVisible) {
      this.sectionVisible = !!isVisible;
      if (this.sectionVisible) {
        this.resumeAutoplay();
      } else {
        this.stopAutoplay();
      }
    }

    setDocumentVisible(isVisible) {
      this.documentVisible = !!isVisible;
      if (this.documentVisible) {
        this.resumeAutoplay();
      } else {
        this.stopAutoplay();
      }
    }

    onEditorSelect() {
      this.editorSelected = true;
      this.stopAutoplay();
    }

    onEditorDeselect() {
      this.editorSelected = false;
      this.resumeAutoplay();
    }

    /* ==========================================================================
       04. SETUP / REBUILD
       ========================================================================== */

    refreshElements() {
      if (!this.root) return;

      this.section = this.root.querySelector('[data-pcmp-slider]') || this.section;
      this.viewport = this.root.querySelector('[data-pcmp-viewport]') || this.viewport;
      this.track = this.root.querySelector('[data-pcmp-track]') || this.track;
      this.prevButton = this.root.querySelector('[data-pcmp-prev]') || this.prevButton;
      this.nextButton = this.root.querySelector('[data-pcmp-next]') || this.nextButton;
    }

    rebuild({ preserveIndex = true, index = 0 } = {}) {
      if (!this.track) return;

      this.stopAutoplay();
      this.clearAutoplayResume();
      this.clearTransitionFallback();

      this.isAnimating = false;
      this.setDraggingState(false);

      this.collectOriginalSlides();
      this.collectRenderedSlides();

      this.visibleColumns = this.getVisibleColumns();
      this.measure();

      const safeIndex = preserveIndex
        ? this.getSafeIndex(index)
        : 0;

      this.currentIndex = safeIndex;
      this.autoplayDirection = this.getNormalizedAutoplayDirection(this.autoplayDirection);

      this.jumpToIndex(this.currentIndex);
      this.updateArrowState();
      this.resumeAutoplay();
    }

    collectOriginalSlides() {
      if (!this.track) {
        this.originalSlides = [];
        return;
      }

      this.originalSlides = Array.from(this.track.children).filter((node) => {
        return node && node.nodeType === 1;
      });
    }

    collectRenderedSlides() {
      this.renderedSlides = this.originalSlides.slice();
    }

    getVisibleColumns() {
      return matchesDesktop()
        ? Math.max(1, Number(this.settings.desktopColumns || 4))
        : Math.max(1, Number(this.settings.mobileColumns || 1));
    }

    measure() {
      const referenceSlide = this.renderedSlides[0] || null;
      const slideWidth = referenceSlide
        ? referenceSlide.getBoundingClientRect().width
        : 0;

      this.stepPx = slideWidth + parseGap(this.track);
    }

    ensureMeasured() {
      if (this.stepPx > 0) return true;
      this.measure();
      return this.stepPx > 0;
    }

    /* ==========================================================================
       05. NAVIGATION / POSITIONING
       ========================================================================== */

    getFiniteMaxIndex() {
      return Math.max(0, this.originalSlides.length - this.visibleColumns);
    }

    getSafeIndex(index) {
      return clamp(index, 0, this.getFiniteMaxIndex());
    }

    canMove() {
      return this.getFiniteMaxIndex() > 0;
    }

    getTranslateXForIndex(index) {
      return -(this.getSafeIndex(index) * this.stepPx);
    }

    setTrackTransitionEnabled(enabled) {
      if (!this.track) return;
      this.track.style.transitionDuration = enabled ? '' : '0ms';
    }

    applyTransform(index, { animate = true } = {}) {
      if (!this.track) return;

      this.setTrackTransitionEnabled(animate);
      this.track.style.transform = `translate3d(${this.getTranslateXForIndex(index)}px, 0, 0)`;
    }

    jumpToIndex(index) {
      if (!this.ensureMeasured() || !this.track) return;

      this.currentIndex = this.getSafeIndex(index);
      this.applyTransform(this.currentIndex, { animate: false });

      void this.track.offsetWidth;

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (!this.track || this.isDestroyed) return;
          this.track.style.transitionDuration = '';
        });
      });
    }

    moveToIndex(index, { animate = true } = {}) {
      if (!this.track || !this.ensureMeasured()) return;

      const safeIndex = this.getSafeIndex(index);
      this.clearTransitionFallback();
      this.currentIndex = safeIndex;
      this.isAnimating = !!animate;
      this.applyTransform(safeIndex, { animate });

      if (!animate) {
        this.isAnimating = false;
        this.updateArrowState();
        return;
      }

      this.startTransitionFallback();
    }

    next({ autoplay = false } = {}) {
      if (!this.canMove() || this.isDragging || this.isAnimating) return;

      const nextIndex = this.getSafeIndex(this.currentIndex + 1);
      if (nextIndex === this.currentIndex) return;

      this.autoplayDirection = 1;
      this.moveToIndex(nextIndex, { animate: true });

      if (!autoplay) {
        this.nudgeAutoplayResume();
      }
    }

    prev({ autoplay = false } = {}) {
      if (!this.canMove() || this.isDragging || this.isAnimating) return;

      const prevIndex = this.getSafeIndex(this.currentIndex - 1);
      if (prevIndex === this.currentIndex) return;

      this.autoplayDirection = -1;
      this.moveToIndex(prevIndex, { animate: true });

      if (!autoplay) {
        this.nudgeAutoplayResume();
      }
    }

    snapBack() {
      this.moveToIndex(this.currentIndex, { animate: true });
    }

    updateArrowState() {
      if (!this.prevButton || !this.nextButton) return;

      const disabledClass = 'is-disabled';
      const disableBoth = !this.canMove();

      if (disableBoth) {
        this.prevButton.disabled = true;
        this.nextButton.disabled = true;
        this.prevButton.classList.add(disabledClass);
        this.nextButton.classList.add(disabledClass);
        return;
      }

      const maxIndex = this.getFiniteMaxIndex();
      const prevDisabled = this.currentIndex <= 0;
      const nextDisabled = this.currentIndex >= maxIndex;

      this.prevButton.disabled = prevDisabled;
      this.nextButton.disabled = nextDisabled;

      this.prevButton.classList.toggle(disabledClass, prevDisabled);
      this.nextButton.classList.toggle(disabledClass, nextDisabled);
    }

    getTransitionFallbackMs() {
      const fromStyles = getFirstTransitionTimeMs(this.track, 'transform');
      if (fromStyles > 0) return fromStyles + TRANSITION_FALLBACK_PADDING;
      return 700;
    }

    startTransitionFallback() {
      this.clearTransitionFallback();

      this.transitionFallbackTimer = window.setTimeout(() => {
        this.transitionFallbackTimer = null;

        if (this.isDestroyed || !this.isAnimating) return;
        this.finishMotionCycle();
      }, this.getTransitionFallbackMs());
    }

    clearTransitionFallback() {
      if (this.transitionFallbackTimer) {
        window.clearTimeout(this.transitionFallbackTimer);
        this.transitionFallbackTimer = null;
      }
    }

    finishMotionCycle() {
      this.clearTransitionFallback();

      if (!this.isAnimating) return;
      this.isAnimating = false;

      this.updateArrowState();

      if (!this.isDragging) {
        this.resumeAutoplay();
      }
    }

    /* ==========================================================================
       06. AUTOPLAY
       ========================================================================== */

    getInitialAutoplayDirection() {
      return String(this.settings.sliderDirection || 'rtl') === 'ltr' ? -1 : 1;
    }

    getNormalizedAutoplayDirection(direction) {
      const maxIndex = this.getFiniteMaxIndex();

      if (maxIndex <= 0) return 1;

      let nextDirection = direction === -1 ? -1 : 1;

      if (this.currentIndex <= 0 && nextDirection < 0) {
        nextDirection = 1;
      }

      if (this.currentIndex >= maxIndex && nextDirection > 0) {
        nextDirection = -1;
      }

      return nextDirection;
    }

    getNextAutoplayIndex() {
      if (!this.canMove()) return this.currentIndex;

      const maxIndex = this.getFiniteMaxIndex();

      this.autoplayDirection = this.getNormalizedAutoplayDirection(
        this.autoplayDirection || this.getInitialAutoplayDirection()
      );

      let nextIndex = this.currentIndex + this.autoplayDirection;

      if (nextIndex > maxIndex) {
        this.autoplayDirection = -1;
        nextIndex = maxIndex - 1;
      } else if (nextIndex < 0) {
        this.autoplayDirection = 1;
        nextIndex = 1;
      }

      return this.getSafeIndex(nextIndex);
    }

    canAutoplay() {
      if (!this.settings.sliderAutoplay) return false;
      if (!this.canMove()) return false;
      if (this.isDragging || this.isAnimating) return false;
      if (this.hoverPaused) return false;
      if (this.editorSelected) return false;
      if (!this.sectionVisible || !this.documentVisible) return false;

      if (this.settings.respectReducedMotion && this.prefersReducedMotion) {
        return false;
      }

      return true;
    }

    queueAutoplayTick() {
      if (!this.canAutoplay()) return;

      this.stopAutoplay();

      const interval = Math.max(
        500,
        Number(this.settings.sliderAutoplayInterval || 2500)
      );

      this.autoplayTimer = window.setTimeout(() => {
        this.autoplayTimer = null;

        if (!this.canAutoplay()) return;

        const targetIndex = this.getNextAutoplayIndex();
        if (targetIndex === this.currentIndex) return;

        this.moveToIndex(targetIndex, { animate: true });
      }, interval);
    }

    startAutoplay() {
      this.queueAutoplayTick();
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        window.clearTimeout(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    clearAutoplayResume() {
      if (this.autoplayResumeTimer) {
        window.clearTimeout(this.autoplayResumeTimer);
        this.autoplayResumeTimer = null;
      }
    }

    resumeAutoplay(delay = 0) {
      this.stopAutoplay();
      this.clearAutoplayResume();

      if (!this.canAutoplay()) return;

      if (delay > 0) {
        this.autoplayResumeTimer = window.setTimeout(() => {
          this.autoplayResumeTimer = null;
          this.queueAutoplayTick();
        }, delay);
        return;
      }

      this.queueAutoplayTick();
    }

    nudgeAutoplayResume() {
      this.resumeAutoplay(AUTOPLAY_RESUME_DELAY);
    }

    /* ==========================================================================
       07. POINTER DRAG / SWIPE
       ========================================================================== */

    isDragEnabledForCurrentViewport() {
      return matchesDesktop()
        ? !!this.settings.dragDesktop
        : !!this.settings.dragMobile;
    }

    setDraggingState(isDragging) {
      this.isDragging = !!isDragging;

      if (this.section) {
        this.section.classList.toggle('is-dragging', this.isDragging);
        this.section.setAttribute(
          'data-pcmp-dragging',
          this.isDragging ? 'true' : 'false'
        );
      }
    }

    onPointerDown(event) {
      if (this.isDestroyed || !this.isDragEnabledForCurrentViewport()) return;
      if (!this.canMove() || this.isAnimating) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (!this.viewport || !this.track) return;

      this.stopAutoplay();
      this.clearAutoplayResume();
      this.clearTransitionFallback();

      this.dragState.pointerId = event.pointerId;
      this.dragState.startX = event.clientX;
      this.dragState.startY = event.clientY;
      this.dragState.currentX = event.clientX;
      this.dragState.deltaX = 0;
      this.dragState.deltaY = 0;
      this.dragState.startTime = Date.now();
      this.dragState.baseTranslateX = this.getTranslateXForIndex(this.currentIndex);
      this.dragState.intent = null;
      this.dragState.moved = false;

      this.setDraggingState(true);
      this.track.style.transitionDuration = '0ms';

      safeCall(this.viewport.setPointerCapture?.bind(this.viewport), event.pointerId);
    }

    onPointerMove(event) {
      if (!this.isDragging) return;
      if (event.pointerId !== this.dragState.pointerId) return;
      if (!this.track) return;

      const deltaX = event.clientX - this.dragState.startX;
      const deltaY = event.clientY - this.dragState.startY;

      this.dragState.currentX = event.clientX;
      this.dragState.deltaX = deltaX;
      this.dragState.deltaY = deltaY;

      if (!this.dragState.intent) {
        if (
          Math.abs(deltaX) < DRAG_INTENT_THRESHOLD &&
          Math.abs(deltaY) < DRAG_INTENT_THRESHOLD
        ) {
          return;
        }

        if (Math.abs(deltaY) > Math.abs(deltaX) + DRAG_CANCEL_Y_EXTRA) {
          this.cancelDrag();
          return;
        }

        this.dragState.intent = 'x';
      }

      if (this.dragState.intent !== 'x') return;

      event.preventDefault();
      this.dragState.moved =
        this.dragState.moved || Math.abs(deltaX) > DRAG_INTENT_THRESHOLD;

      const translateX = this.dragState.baseTranslateX + deltaX;
      this.track.style.transform = `translate3d(${translateX}px, 0, 0)`;
    }

    onPointerUp(event) {
      if (!this.isDragging) return;
      if (event.pointerId !== this.dragState.pointerId) return;

      const deltaX = this.dragState.deltaX;
      const elapsed = Math.max(1, Date.now() - this.dragState.startTime);
      const velocityX = deltaX / elapsed;
      const threshold = Math.max(28, this.stepPx * 0.18);

      safeCall(this.viewport?.releasePointerCapture?.bind(this.viewport), event.pointerId);

      this.track.style.transitionDuration = '';

      if (this.dragState.intent === 'x' && this.dragState.moved) {
        this.clickGuardUntil = Date.now() + 220;
        this.resetDragState();

        if (Math.abs(deltaX) > threshold || Math.abs(velocityX) > 0.6) {
          if (deltaX < 0) {
            this.next();
          } else {
            this.prev();
          }
          return;
        }

        this.snapBack();
        this.nudgeAutoplayResume();
        return;
      }

      this.resetDragState();
      this.snapBack();
      this.resumeAutoplay();
    }

    onPointerCancel(event) {
      if (!this.isDragging) return;
      if (event.pointerId !== this.dragState.pointerId) return;

      safeCall(this.viewport?.releasePointerCapture?.bind(this.viewport), event.pointerId);

      this.track.style.transitionDuration = '';
      this.resetDragState();
      this.snapBack();
      this.resumeAutoplay();
    }

    onPointerLeave(event) {
      if (!this.isDragging) return;
      if (event.pointerType && event.pointerType !== 'mouse') return;
      this.onPointerCancel(event);
    }

    cancelDrag() {
      if (!this.isDragging) return;

      this.track.style.transitionDuration = '';
      this.resetDragState();
      this.snapBack();
      this.resumeAutoplay();
    }

    resetDragState() {
      this.dragState.pointerId = null;
      this.dragState.startX = 0;
      this.dragState.startY = 0;
      this.dragState.currentX = 0;
      this.dragState.deltaX = 0;
      this.dragState.deltaY = 0;
      this.dragState.startTime = 0;
      this.dragState.baseTranslateX = 0;
      this.dragState.intent = null;
      this.dragState.moved = false;

      this.setDraggingState(false);
    }

    /* ==========================================================================
       08. EVENT HANDLERS
       ========================================================================== */

    onPrevClick(event) {
      event.preventDefault();
      if (this.isAnimating || this.isDragging) return;
      this.prev();
    }

    onNextClick(event) {
      event.preventDefault();
      if (this.isAnimating || this.isDragging) return;
      this.next();
    }

    onMouseEnter() {
      if (!matchesDesktop()) return;
      if (!this.settings.pauseOnHoverDesktop) return;

      this.hoverPaused = true;
      this.stopAutoplay();
    }

    onMouseLeave() {
      if (!matchesDesktop()) return;
      if (!this.settings.pauseOnHoverDesktop) return;

      this.hoverPaused = false;
      this.resumeAutoplay();
    }

    onTrackTransitionEnd(event) {
      if (!event || event.target !== this.track) return;
      if (event.propertyName && event.propertyName !== 'transform') return;
      this.finishMotionCycle();
    }

    onTrackTransitionCancel(event) {
      if (!event || event.target !== this.track) return;
      if (event.propertyName && event.propertyName !== 'transform') return;
      this.finishMotionCycle();
    }

    onClickCapture(event) {
      if (Date.now() < this.clickGuardUntil) {
        event.preventDefault();
        event.stopPropagation();
      }
    }

    onDocumentVisibilityChange() {
      this.setDocumentVisible(document.visibilityState !== 'hidden');
    }

    onWindowResize() {
      this.scheduleLayoutRefresh();
    }

    onResizeObserved() {
      this.scheduleLayoutRefresh();
    }

    /* ==========================================================================
       09. OBSERVERS / LAYOUT REFRESH
       ========================================================================== */

    scheduleLayoutRefresh() {
      if (this.layoutRaf || this.isDestroyed) return;

      this.layoutRaf = window.requestAnimationFrame(() => {
        this.layoutRaf = null;

        if (this.isDestroyed || this.isDragging || this.isAnimating) return;

        this.rebuild({
          preserveIndex: true,
          index: this.currentIndex,
        });
      });
    }

    clearScheduledLayout() {
      if (this.layoutRaf) {
        window.cancelAnimationFrame(this.layoutRaf);
        this.layoutRaf = null;
      }
    }

    setupObservers() {
      document.addEventListener('visibilitychange', this.onDocumentVisibilityChange);

      if (window.ResizeObserver) {
        this.resizeObserver = new window.ResizeObserver(this.onResizeObserved);
        if (this.viewport) this.resizeObserver.observe(this.viewport);
        if (this.track) this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', this.onWindowResize, { passive: true });
      }
    }

    teardownObservers() {
      document.removeEventListener('visibilitychange', this.onDocumentVisibilityChange);
      window.removeEventListener('resize', this.onWindowResize);

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
    }

    /* ==========================================================================
       10. DOM BINDINGS
       ========================================================================== */

    bind() {
      this.prevButton?.addEventListener('click', this.onPrevClick);
      this.nextButton?.addEventListener('click', this.onNextClick);

      this.viewport?.addEventListener('pointerdown', this.onPointerDown, {
        passive: true,
      });
      this.viewport?.addEventListener('pointermove', this.onPointerMove, {
        passive: false,
      });
      this.viewport?.addEventListener('pointerup', this.onPointerUp);
      this.viewport?.addEventListener('pointercancel', this.onPointerCancel);
      this.viewport?.addEventListener('pointerleave', this.onPointerLeave);

      this.viewport?.addEventListener('mouseenter', this.onMouseEnter);
      this.viewport?.addEventListener('mouseleave', this.onMouseLeave);

      this.viewport?.addEventListener('click', this.onClickCapture, true);
      this.track?.addEventListener('transitionend', this.onTrackTransitionEnd);
      this.track?.addEventListener('transitioncancel', this.onTrackTransitionCancel);
    }

    unbind() {
      this.prevButton?.removeEventListener('click', this.onPrevClick);
      this.nextButton?.removeEventListener('click', this.onNextClick);

      this.viewport?.removeEventListener('pointerdown', this.onPointerDown);
      this.viewport?.removeEventListener('pointermove', this.onPointerMove);
      this.viewport?.removeEventListener('pointerup', this.onPointerUp);
      this.viewport?.removeEventListener('pointercancel', this.onPointerCancel);
      this.viewport?.removeEventListener('pointerleave', this.onPointerLeave);

      this.viewport?.removeEventListener('mouseenter', this.onMouseEnter);
      this.viewport?.removeEventListener('mouseleave', this.onMouseLeave);

      this.viewport?.removeEventListener('click', this.onClickCapture, true);
      this.track?.removeEventListener('transitionend', this.onTrackTransitionEnd);
      this.track?.removeEventListener('transitioncancel', this.onTrackTransitionCancel);
    }
  }

  /* ==========================================================================
     11. WINDOW EXPORT
     ========================================================================== */

  window.PCMPProductSlider = PCMPProductSlider;
})();